import React, { useState, useCallback, useRef, useEffect } from "react";
import { HashRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Home from "./Home";
import ChatPage from "./components/chat/ChatPage";
import "./App.css";
import { Provider, getDefaultStore } from "jotai";
import KnowledgePage from "./knowledge/generate_knowledge";
import GoogleMapComponent from "./googlemap/GoogleMapComponent";
import ChatHistoryGraph from "./components/ChatHistoryGraph";
import DifyAPITestPage from "./components/difyAPITest/DifyAPITestPage";
import { LoadScript } from "@react-google-maps/api";
import { api } from "~/api";
import LoadingScreen from "./components/LoadingScreen";
import SettingPage from "./setting/Setting";
import ReceptionConversationDepartmentPic from "./reception/ReceptionConversationDepartmentPic";
import ReceptionConversationVisitor from "./reception/ReceptionConversationVisitor";
import CompanyListPage from "./reception/CompanyListPage";
import InputReceptionCodePage from "./reception/InputReceptionCodePage";
import ReceptionLoadingPage from "./reception/ReceptionLoadingPage";
import VisitorConfirmationPage from "./reception/VisitorConfirmationPage";
import VisitorSelectionPage from "./reception/VisitorSelectionPage";
import VisitorInfoInputPage from "./reception/VisitorInfoInputPage";
import CallingPhonePage from "./reception/CallingPhonePage";
import DeliveryMapPage from "./reception/DeliveryMapPage";
import ReceptionQrScanPage from "./reception/ReceptionQrScanPage";
import HealthCheckPage from "~/health/HealthCheckPage";
import { useDynamoDBAtom } from "./utils/atomWithDynamoDBUtil";
import { AuthProvider, ProtectedRoute, useAuth } from "./components/auth/CognitoAuth";
import NotificationDialog from "./components/NotificationDialog";
import MenuReceptionPage from "./reception/MenuReceptionPage";
import InputExtensionNumberPage from "./reception/InputExtensionNumberPage";
import CompleteReceptionPage from "./reception/CompleteReceptionPage";
import { ROUTE } from "~/constants/routes";

import {
  waitTimerAtom,
  talkingModeAtom,
  listeningModeAtom,
  promptAtom,
  isFirstResponseAtom,
  isCallOutAtom,
  logoSrcAtom,
  locationInfoAtom,
  qrCodeTitleAtom,
  qrCodeUrlAtom,
  floorImagesAtom,
  isGreeterAtom,
  styleModeAtom,
  customImagesAtom,
  feedbackModeAtom,
  dynamoDBAtoms,
  getCurrentCustomerId,
} from "./AtomWithDynamoDB";
import DeliveryCompaniesListPage from "./reception/DeliveryCompaniesListPage";
import ErrorPage from "./reception/ErrorPage";
import { isReceptionAtom } from "~/Atom";
import { useAtom } from "jotai";

/**
 * Atom初期化を行うコンポーネント（Provider内で実行）
 * すべてのDynamoDB対応Atomの初期化が完了したらonCompleteを呼び出す
 * @param onComplete 初期化完了時に呼び出されるコールバック
 * @return null
 */
const AtomInitializer: React.FC<{ onComplete: () => void }> = ({ onComplete }) => {
  const hasInitialized = useRef(false);
  const [initializedCount, setInitializedCount] = useState<number>(0);
  const dynamoDBAtomsCount = dynamoDBAtoms.length;
  const completedAtomsRef = useRef(new Set<string>());

  // 各Atomの初期化完了時にカウントアップ
  const handleAtomInitialized = useCallback(
    (atomKey: string) => {
      // 重複カウント防止
      if (completedAtomsRef.current.has(atomKey)) {
        return;
      }

      completedAtomsRef.current.add(atomKey);
      setInitializedCount((prev) => {
        const newCount = prev + 1;
        return newCount;
      });
    },
    [dynamoDBAtomsCount]
  );

  // すべてのDynamoDB対応Atomを初期化
  useDynamoDBAtom(waitTimerAtom, () => handleAtomInitialized("waitTimer"));
  useDynamoDBAtom(talkingModeAtom, () => handleAtomInitialized("talkingMode"));
  useDynamoDBAtom(listeningModeAtom, () => handleAtomInitialized("listeningMode"));
  useDynamoDBAtom(isFirstResponseAtom, () => handleAtomInitialized("isFirstResponse"));
  useDynamoDBAtom(isCallOutAtom, () => handleAtomInitialized("isCallOut"));
  useDynamoDBAtom(locationInfoAtom, () => handleAtomInitialized("locationInfo"));
  useDynamoDBAtom(promptAtom, () => handleAtomInitialized("prompt"));
  useDynamoDBAtom(logoSrcAtom, () => handleAtomInitialized("logoSrc"));
  useDynamoDBAtom(qrCodeTitleAtom, () => handleAtomInitialized("qrCodeTitle"));
  useDynamoDBAtom(qrCodeUrlAtom, () => handleAtomInitialized("qrCodeUrl"));
  useDynamoDBAtom(floorImagesAtom, () => handleAtomInitialized("floorImages"));
  useDynamoDBAtom(isGreeterAtom, () => handleAtomInitialized("isGreeter"));
  useDynamoDBAtom(styleModeAtom, () => handleAtomInitialized("styleMode"));
  useDynamoDBAtom(customImagesAtom, () => handleAtomInitialized("customImages"));
  useDynamoDBAtom(feedbackModeAtom, () => handleAtomInitialized("feedbackMode"));

  // 全Atomの初期化が完了したら通知
  React.useEffect(() => {
    if (initializedCount === dynamoDBAtomsCount && !hasInitialized.current) {
      hasInitialized.current = true;
      onComplete();
    }
  }, [initializedCount, dynamoDBAtomsCount, onComplete]);

  return null;
};

/**
 * Google Maps APIキーを取得してLoadScriptでラップするコンポーネント
 * @param children ラップする子コンポーネント
 */
const GoogleMapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [apiKey, setApiKey] = useState<string>("");
  const { isAuthenticated, isLoading } = useAuth();
  const systemRequirement = process.env.REACT_APP_SYSTEM_REQUIREMENT;

  // Google Maps APIキーを取得する非同期関数
  const fetchGoogleMapKey = useCallback(async () => {
    const customerId = await getCurrentCustomerId();
    const key = await api.googlemap.getKey.query({ customerId });
    return key;
  }, []);

  useEffect(() => {
    // AWS環境の場合は認証状態が確定するまで待機
    if (systemRequirement === "aws") {
      if (isLoading || !isAuthenticated) {
        return;
      }
    }

    // APIキーを取得
    fetchGoogleMapKey()
      .then((key) => {
        setApiKey(key);
      })
      .catch((error) => {
        console.error("Error fetching Google Maps API key:", error);
      });
  }, [isAuthenticated, isLoading, systemRequirement]);

  // APIキー未取得時はローディング画面を表示
  if (!apiKey) {
    return <LoadingScreen />;
  }

  return (
    <LoadScript googleMapsApiKey={apiKey} libraries={["places", "geometry"]}>
      {children}
    </LoadScript>
  );
};

/**
 * prod(stgを含む)環境でホワイトリスト外のルートに直接アクセスされた場合に/#/chatへリダイレクトするガード
 * 追加する場合はPROD_ROUTE_WHITELISTに追記する
 *
 * @returns null
 */
const PROD_ROUTE_WHITELIST: string[] = ["/chat", "/setting", "/reception/loading"];

const ProdRouteGuard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isProd = process.env.REACT_APP_ENVIRONMENT === "prod";
  // 初回ロード時（URL直接入力・ブックマーク・リロード）のpathnameのみ保持する
  const initialPathnameRef = useRef(location.pathname);

  // 初回マウント時のみ判定し、アプリ内遷移では再実行しない
  useEffect(() => {
    const pathname = initialPathnameRef.current;
    if (isProd && !PROD_ROUTE_WHITELIST.includes(pathname)) {
      const target = pathname.includes("/reception") ? "/reception/loading" : "/chat";
      navigate(target, { replace: true });
    }
  }, []);

  return null;
};

const ReceptionRouteStateSync: React.FC = () => {
  const location = useLocation();
  const [, setIsReception] = useAtom(isReceptionAtom);

  useEffect(() => {
    setIsReception(location.pathname.startsWith("/reception"));
  }, [location.pathname, setIsReception]);

  return null;
};

const App: React.FC = () => {
  // DynamoDB対応Atomの初期化完了フラグ
  const [isAtomsInitialized, setIsAtomsInitialized] = useState<boolean>(false);

  // 環境変数をチェックして認証の必要性を判定
  const isOnPrem = process.env.REACT_APP_SYSTEM_REQUIREMENT === "onprem";

  // ルートコンポーネントを条件分岐で決定
  const RouteWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if (isOnPrem) {
      // オンプレミス環境では認証なしで直接表示
      return <>{children}</>;
    } else {
      // クラウド環境では認証が必要
      return <ProtectedRoute>{children}</ProtectedRoute>;
    }
  };

  return (
    <HashRouter
      future={{
        /* eslint-disable camelcase */
        v7_startTransition: true,
        v7_relativeSplatPath: true,
        /* eslint-enable camelcase */
      }}
    >
      <ProdRouteGuard />
      <AuthProvider>
        <Provider store={getDefaultStore()}>
          <ReceptionRouteStateSync />
          <NotificationDialog />
          <RouteWrapper>
            {/* DynamoDB対応Atomの初期化（Provider内で実行） */}
            <AtomInitializer
              onComplete={() => {
                setIsAtomsInitialized(true);
              }}
            />

            {/* Atom初期化の間、ローディング画面を表示 */}
            {!isAtomsInitialized ? (
              <LoadingScreen />
            ) : (
              <div className="App">
                <GoogleMapsProvider>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path={ROUTE.RECEPTION.LOADING} element={<ReceptionLoadingPage />} />
                    <Route
                      path={ROUTE.RECEPTION.CONVERSATION.VISITOR_CONFIRMATION}
                      element={<VisitorConfirmationPage />}
                    />
                    <Route
                      path={ROUTE.RECEPTION.CONVERSATION.VISITOR_SELECTION}
                      element={<VisitorSelectionPage />}
                    />
                    <Route
                      path={ROUTE.RECEPTION.CONVERSATION.VISITOR}
                      element={<ReceptionConversationVisitor />}
                    />
                    <Route
                      path={ROUTE.RECEPTION.CONVERSATION.DEPARTMENT_PIC}
                      element={<ReceptionConversationDepartmentPic />}
                    />
                    <Route
                      path={ROUTE.RECEPTION.VISITOR_INFO_INPUT}
                      element={<VisitorInfoInputPage />}
                    />
                    <Route path={ROUTE.RECEPTION.QR} element={<ReceptionQrScanPage />} />
                    <Route path={ROUTE.RECEPTION.MENU} element={<MenuReceptionPage />} />
                    <Route
                      path={ROUTE.RECEPTION.DELIVERY_COMPANIES_LIST}
                      element={<DeliveryCompaniesListPage />}
                    />
                    <Route path={ROUTE.RECEPTION.DELIVERY_MAP} element={<DeliveryMapPage />} />
                    <Route path={ROUTE.RECEPTION.COMPLETE} element={<CompleteReceptionPage />} />
                    <Route path={ROUTE.RECEPTION.INPUT_CODE} element={<InputReceptionCodePage />} />
                    <Route path={ROUTE.RECEPTION.COMPANY} element={<CompanyListPage />} />
                    <Route path={ROUTE.RECEPTION.CALLING} element={<CallingPhonePage />} />
                    <Route path={ROUTE.RECEPTION.ERROR} element={<ErrorPage />} />
                    <Route path="/chat" element={<ChatPage />} />
                    <Route path="/knowledge" element={<KnowledgePage />} />
                    <Route path="/googlemap" element={<GoogleMapComponent />} />
                    <Route path="/analytics" element={<ChatHistoryGraph />} />
                    <Route path="/setting" element={<SettingPage />} />
                    <Route path="/healthcheck" element={<HealthCheckPage />} />
                    <Route path="/dify-api-test" element={<DifyAPITestPage />} />

                    <Route
                      path={ROUTE.RECEPTION.INPUT_EXTENSION_NUMBER}
                      element={<InputExtensionNumberPage />}
                    />
                  </Routes>
                </GoogleMapsProvider>
              </div>
            )}
          </RouteWrapper>
        </Provider>
      </AuthProvider>
    </HashRouter>
  );
};

export default App;
